import { sleep } from '../utils/importUtils';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AccountingEngine } from './accountingEngine';

export const PIPELINE_STEPS = ['Structure Validation', 'Row Processing', 'Duplicate Checking', 'Render View'];
export const CHUNK_SIZE = 75;

export const DATE_FORMATS = [
  { re: /^(\d{4})-(\d{2})-(\d{2})$/, parse: (m: any) => new Date(+m[1], +m[2]-1, +m[3]) },
  { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, parse: (m: any) => new Date(+m[3], +m[2]-1, +m[1]) },
  { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, parse: (m: any) => new Date(+m[3], +m[2]-1, +m[1]) },
];

export const LEDGER_SUGGESTION_RULES = [
  { test: (t: string, type: string) => type === 'SALE' && /ltd|pvt|private|limited|llp|inc/i.test(t), ledger: 'Sundry Debtors' },
  { test: (t: string, type: string) => type === 'PURCHASE' && /ltd|pvt|private|limited|llp|inc/i.test(t), ledger: 'Sundry Creditors' },
];

export function suggestLedger(party: string, type: string) {
  if (!party) return null;
  for (const rule of LEDGER_SUGGESTION_RULES) {
    if (rule.test(party, type)) return rule.ledger;
  }
  return type === 'SALE' ? 'Sundry Debtors' : 'Sundry Creditors';
}

// ─── Ledger Memory (in-memory, session-scoped) ────────────────────────────────
const PARTY_LEDGER_MEMORY: Record<string, string> = {};

export async function fetchLedgerMemory() {
  await sleep(100);
  return { ...PARTY_LEDGER_MEMORY };
}

export async function saveLedgerMemory(party: string, ledger: string) {
  if (party && ledger) PARTY_LEDGER_MEMORY[party.toUpperCase()] = ledger;
}

// ─── Fetch real ledgers from window.db ────────────────────────────────────────
export async function fetchLedgers(): Promise<string[]> {
  const company = useStore.getState().activeCompany;
  if (!company) return [
    'Sales Account', 'Purchase Expenses', 'Salary Account', 'Rent Account',
    'Miscellaneous Expenses', 'Consulting Fees', 'Office Maintenance',
    'Advertising Expense', 'Software Subscriptions', 'Legal Services',
    'Fuel Expense', 'Bank Charges', 'Interest Income', 'Sundry Debtors',
    'Sundry Creditors', 'Capital Account', 'Drawings', 'Loan Account',
    'Tax Payable', 'Depreciation', 'Insurance Premium', 'Freight Charges',
    'Commission Income', 'Repairs & Maintenance', 'Printing & Stationery',
  ];
  
  try {
    const ledgers = await db.ledgers.where('companyId').equals(company.id!).toArray();
    const names = ledgers
      .filter((l: any) => !l.isDeleted)
      .map((l: any) => l.name as string)
      .sort();
      
    if (names.length > 0) return names;
  } catch (e) {
    console.warn(`[ImportInvoice] fetchLedgers failed:`, e);
  }
  return [
    'Sales Account', 'Purchase Expenses', 'Salary Account', 'Rent Account',
    'Miscellaneous Expenses', 'Consulting Fees', 'Office Maintenance',
    'Advertising Expense', 'Software Subscriptions', 'Legal Services',
    'Fuel Expense', 'Bank Charges', 'Interest Income', 'Sundry Debtors',
    'Sundry Creditors', 'Capital Account', 'Drawings', 'Loan Account',
    'Tax Payable', 'Depreciation', 'Insurance Premium', 'Freight Charges',
    'Commission Income', 'Repairs & Maintenance', 'Printing & Stationery',
  ];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
async function getLedgerByTerm(companyId: number, name: string): Promise<any> {
    if (!name?.trim()) return null;
    const ledgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    const active = ledgers.filter((l: any) => !l.isDeleted);
    const nameLower = name.trim().toLowerCase();

    // 1. Exact match (case-insensitive)
    const exact = active.find((l: any) => l.name?.trim().toLowerCase() === nameLower);
    if (exact) return exact;

    // 2. Short name match — strip em-dash suffix
    const shortName = nameLower.split('—')[0].trim();
    const shortMatch = active.find((l: any) => l.name?.trim().toLowerCase() === shortName);
    if (shortMatch) return shortMatch;

    // 3. Prefer ledger whose name starts with the search term
    const startsMatch = active.find((l: any) => l.name?.trim().toLowerCase().startsWith(nameLower));
    if (startsMatch) return startsMatch;

    // 4. Substring match — only if unique
    const subMatches = active.filter((l: any) => l.name && l.name.toLowerCase().includes(nameLower));
    if (subMatches.length === 1) return subMatches[0];

    // 5. Reverse substring — only if unique
    const reverseMatches = active.filter((l: any) => l.name && nameLower.includes(l.name.toLowerCase()));
    if (reverseMatches.length === 1) return reverseMatches[0];

    return null;
}

function ddmmyyyyToISO(date: string): string {
  if (!date) return new Date().toISOString().split('T')[0];
  if (date.includes('/')) {
    const [dd, mm, yyyy] = date.split('/');
    if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  if (date.includes('-')) {
    if (date.length === 10 && date[4] === '-') return date; // already ISO: YYYY-MM-DD
    const [dd, mm, yyyy] = date.split('-');
    if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  return date;
}

// ─── Real voucher creation via window.db ──────────────────────────────────────
export async function apiCreateVouchers(
  payloads: any[]
): Promise<{ success: boolean; rowIdx: number; refId: string | null; error?: string }[]> {
  const results: { success: boolean; rowIdx: number; refId: string | null; error?: string }[] = [];

  const company = useStore.getState().activeCompany;
  if (!company) {
      return payloads.map(p => ({ success: false, rowIdx: p.rowIdx, refId: null, error: 'Database not loaded. Open a company first.' }));
  }

  for (const p of payloads) {
    try {
      // ── Pre-flight validations ──────────────────────────────────────────────
      const amount = Number(p.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error(`Invalid amount: "${p.amount}". Must be a positive number.`);
      }

      if (!p.type || (p.type !== 'SALE' && p.type !== 'PURCHASE')) {
        throw new Error(`Unknown voucher type: "${p.type}". Expected SALE or PURCHASE.`);
      }

      const voucherType = p.type === 'SALE' ? 'Sales' : 'Purchase';

      // Resolve ledger IDs
      const partyLedger = await getLedgerByTerm(company.id!, p.party);
      if (!partyLedger) throw new Error(`Party ledger not found: "${p.party}". Create it first.`);

      const matchedLedger = await getLedgerByTerm(company.id!, p.matchedLedger);
      if (!matchedLedger) throw new Error(`Ledger not found: "${p.matchedLedger}".`);

      // Resolve voucherTypeId for correct numbering
      const vt = await db.voucherTypes
        .where('[companyId+name]').equals([company.id!, voucherType]).first();

      // Double-entry accounting
      // SALE:     Party A/c Dr  | Matched (Sales) Cr
      // PURCHASE: Matched (Purchase) Dr | Party A/c Cr
      const ledgerEntries = voucherType === 'Sales'
        ? [
            { ledgerId: partyLedger.id,   amount, type: 'Dr' as const },
            { ledgerId: matchedLedger.id, amount, type: 'Cr' as const },
          ]
        : [
            { ledgerId: matchedLedger.id, amount, type: 'Dr' as const },
            { ledgerId: partyLedger.id,   amount, type: 'Cr' as const },
          ];

      // ── Explicit Dr = Cr assertion (defence-in-depth before DB write) ───────
      const totalDr = ledgerEntries.filter(e => e.type === 'Dr').reduce((s, e) => s + e.amount, 0);
      const totalCr = ledgerEntries.filter(e => e.type === 'Cr').reduce((s, e) => s + e.amount, 0);
      if (Math.abs(totalDr - totalCr) >= 0.01) {
        throw new Error(`Double-entry imbalance: Dr ₹${totalDr.toFixed(2)} ≠ Cr ₹${totalCr.toFixed(2)}. Please report this bug.`);
      }

      const vouchObj = {
        companyId: company.id!,
        type: voucherType,
        voucherTypeId: vt?.id,
        date: ddmmyyyyToISO(p.date),
        narration: p.narration || `Imported ${p.type} — ${p.party}`,
      };

      const newId = await AccountingEngine.postVoucher(vouchObj, ledgerEntries);
      const newVoucher = await db.vouchers.get(newId);

      results.push({ success: true, rowIdx: p.rowIdx, refId: newVoucher?.voucherNumber || String(newId) });
    } catch (e: any) {
      console.error('[ImportInvoice] apiCreateVouchers error:', e);
      results.push({ success: false, rowIdx: p.rowIdx, refId: null, error: e.message });
    }
  }

  // Trigger db updated
  window.dispatchEvent(new Event('db-updated'));

  return results;
}